import { useEffect, useRef } from 'react';
import { buildPositionChangesFromAutoPositions } from '../positionPersistence/positionChangeBuilders';
import { flushTidyPositionChanges } from '../positionPersistence/flushTidySaves';
import { SNAP_SAVED_TO_ELK_MIN_DELTA_PX } from './constants';
import { markCampaignTidyManaged } from './tidyManagedCampaigns';

function positionsSignature(tasks, autoPositions) {
  return tasks
    .map((t) => {
      const p = autoPositions[t.id];
      return `${t.id}:${Math.round(p?.x ?? 0)}:${Math.round(p?.y ?? 0)}`;
    })
    .sort()
    .join('|');
}

/**
 * After ELK settles with real measured dimensions, reconcile saved positions with the
 * fresh ELK layout.
 *
 * Measured card sizes are never bit-identical across sessions (font timing, nested-campaign
 * progress chrome, content changes), so ELK re-derives a layout offset from what the last
 * Tidy persisted. Left alone, that drift keeps edges off the ELK router and makes every
 * manual Tidy visibly nudge the board.
 *
 * Flow view always owns placement via ELK. Follow the fresh layout whenever it
 * diverges — including after add/delete/connect and in-session dimension changes.
 * Mid-drag, filters, and multi-campaign are skipped.
 */
export function useSnapSavedToElkOnSettle({
  isMultiCampaignMode,
  hasActiveFilters,
  measuredElkSettled,
  useAutoPositions,
  campaignId,
  tasks,
  autoPositions,
  interaction,
  onPositionChangeBatch,
  onPositionChange,
  draggingNodeRef,
  committedElkLayoutVersion,
  layoutHoldRef = null,
}) {
  const snapDoneRef = useRef(false);
  /** Task-id set for the last snap; graph changes reset so the new ELK result can persist. */
  const openTaskIdSigRef = useRef(null);
  /** Last auto layout flushed to persistence — avoids duplicate writes while round-tripping. */
  const lastFlushedSigRef = useRef(null);

  useEffect(() => {
    snapDoneRef.current = false;
    openTaskIdSigRef.current = null;
    lastFlushedSigRef.current = null;
  }, [campaignId]);

  useEffect(() => {
    if (isMultiCampaignMode || hasActiveFilters) return;
    if (layoutHoldRef?.current) return;
    if (!measuredElkSettled || useAutoPositions) return;
    if (draggingNodeRef?.current != null && draggingNodeRef.current !== undefined) return;
    if (!tasks?.length || !autoPositions) return;

    const taskIdSig = tasks
      .map((t) => t.id)
      .sort()
      .join(',');
    if (openTaskIdSigRef.current === null) {
      openTaskIdSigRef.current = taskIdSig;
    } else if (taskIdSig !== openTaskIdSigRef.current) {
      openTaskIdSigRef.current = taskIdSig;
      snapDoneRef.current = false;
    }

    const custom = interaction?.customPositions;

    let anyMeaningfulDelta = false;
    const displayPositions = { ...autoPositions };
    for (const task of tasks) {
      const auto = autoPositions[task.id];
      if (!auto || !Number.isFinite(auto.x) || !Number.isFinite(auto.y)) return;
      const c = custom?.[task.id];
      const spawnX = Number.isFinite(c?.x) ? c.x : task.x;
      const spawnY = Number.isFinite(c?.y) ? c.y : task.y;
      // A mission with no saved coordinates means the board is incomplete.
      // useTidyWhenUnpositioned runs a full tidy for that case; rewriting the
      // rest here would persist the pre-tidy pack.
      if (!Number.isFinite(spawnX) || !Number.isFinite(spawnY)) return;

      const dx = Math.abs(spawnX - auto.x);
      const dy = Math.abs(spawnY - auto.y);
      if (dx > SNAP_SAVED_TO_ELK_MIN_DELTA_PX || dy > SNAP_SAVED_TO_ELK_MIN_DELTA_PX) {
        anyMeaningfulDelta = true;
      }
    }

    snapDoneRef.current = true;
    if (!anyMeaningfulDelta) return;

    // Align display immediately (same mechanism as Tidy persist) so edges use ELK pathD
    // this render; the saved coordinates catch up when the flush round-trips.
    if (interaction?.setCustomPositions) {
      interaction.setCustomPositions(displayPositions);
    }

    const autoSig = positionsSignature(tasks, autoPositions);
    if (autoSig === lastFlushedSigRef.current) return;
    lastFlushedSigRef.current = autoSig;

    const positionChanges = buildPositionChangesFromAutoPositions(tasks, autoPositions).filter(
      ({ position, oldPosition }) =>
        !oldPosition ||
        Math.abs(position.x - oldPosition.x) > SNAP_SAVED_TO_ELK_MIN_DELTA_PX ||
        Math.abs(position.y - oldPosition.y) > SNAP_SAVED_TO_ELK_MIN_DELTA_PX,
    );
    flushTidyPositionChanges(
      positionChanges,
      { skipRecordAction: true },
      onPositionChangeBatch,
      onPositionChange,
    );
    // A successful silent snap proves the board follows ELK — remember that so future
    // dimension-driven layout shifts (any size) re-align without a manual Tidy.
    markCampaignTidyManaged(campaignId);
  }, [
    committedElkLayoutVersion,
    measuredElkSettled,
    useAutoPositions,
    isMultiCampaignMode,
    hasActiveFilters,
    campaignId,
    tasks,
    autoPositions,
    interaction,
    onPositionChangeBatch,
    onPositionChange,
    draggingNodeRef,
    layoutHoldRef,
  ]);
}
